import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, TextField, MenuItem
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../../Components/spinner/spinner';

export const AdminFeCAComponent = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [feBaseList, setFeBaseList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    ca: '',
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [userSearch, setUserSearch] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // Columns for the TableComponent with UUID lookups
  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    {
      id: 'financial_establishment',
      field: 'financial_establishment',
      headerName: 'FE Base',
      width: 200,
      renderCell: (params) => {
        const feBase = feBaseList.find(fe => fe.id === params.value);
        return feBase ? feBase.name_of_fe : 'N/A';
      },
    },
    {
      id: 'ca',
      field: 'ca',
      headerName: 'CA Email',
      width: 230,
      renderCell: (params) => {
        const user = userList.find(user => user.id === params.value);
        return user ? user.email : 'N/A';
      },
    },
    {
      id: 'is_active',
      field: 'is_active',
      headerName: 'Active',
      width: 80,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={loading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={loading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  // Fetch FeCA records list
  // const fetchFeCAList = () => {
  //   setLoading(true);
  //   api.get(`${API_BASE_URL}/fe-mgmt/fe-ca/`)
  //     .then(res => {
  //       const feCAData = res.data.map((item, index) => ({
  //         id: item.id,
  //         slNo: index + 1,
  //         ...item,
  //       }));
  //       setRows(feCAData);
  //       setLoading(false);
  //     })
  //     .catch(() => {
  //       setSnackbarMessage('Failed to fetch FeCA records.');
  //       setSnackbarSeverity('error');
  //       setSnackbarOpen(true);
  //       setLoading(false);
  //     });
  // };

  const fetchFeCAList = async () => {
  setLoading(true);
  try {
    const [feCAResponse, feBaseResponse, userResponse] = await Promise.all([
      api.get(`/fe-mgmt/fe-ca/`),
      api.get(`/fe-mgmt/fe-base/`),
      api.get(`/auth/role-mgmt/`)
    ]);

    const feBaseMap = new Map(feBaseResponse.data.map(fe => [fe.id, fe.name_of_fe]));
    const userMap = new Map(userResponse.data.map(user => [user.id, user.email]));

    const feCAData = feCAResponse.data.map((item, index) => ({
      id: item.id,
      slNo: index + 1,
      financial_establishment: item.financial_establishment,
      ca: item.ca,
      is_active: item.is_active,
      fe_base_name: feBaseMap.get(item.financial_establishment) || 'N/A',
      ca_email: userMap.get(item.ca) || 'N/A'
    }));

    setFeBaseList(feBaseResponse.data);
    setUserList(userResponse.data);
    setRows(feCAData);
  } catch (error) {
    setSnackbarMessage('Failed to fetch FeCA records or related data.');
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  } finally {
    setLoading(false);
  }
};


  // Fetch FE Base list for dropdown & lookup
  const fetchFeBaseList = () => {
    api.get(`/fe-mgmt/fe-base/`)
      .then(res => setFeBaseList(res.data || []))
      .catch(() => setFeBaseList([]));
  };

  // Fetch user list for CA assignment and lookup
  const fetchUserList = () => {
    api.get(`/auth/role-mgmt/`)
      .then(res => setUserList(res.data || []))
      .catch(() => setUserList([]));
  };

  // Initial data fetch on mount
  useEffect(() => {
    fetchFeCAList();
    fetchFeBaseList();
    fetchUserList();
  }, []);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Show Add form dialog
  const handleAdd = () => {
    setIsEditMode(false);
    setFormData({ financial_establishment: '', ca: '', is_active: true });
    setFormErrors({});
    setUserSearch('');
    setOpenFormDialog(true);
  };

  // Show Edit form dialog with selected item data
  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    setFormData({
      financial_establishment: item.financial_establishment || '',
      ca: item.ca || '',
      is_active: !!item.is_active,
    });
    setFormErrors({});
    setUserSearch('');
    setOpenFormDialog(true);
  };

  // Validate the form before submit
  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment) errors.financial_establishment = 'FE Base is required';
    if (!formData.ca) errors.ca = 'CA is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission for add/edit
  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-ca/${selectedItem.id}/`, formData);
        setSnackbarMessage('FeCA record updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-ca/`, formData);
        setSnackbarMessage('FeCA record created successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchFeCAList();
      setOpenFormDialog(false);
    } catch {
      setSnackbarMessage('Failed to save FeCA record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Confirm delete dialog open
  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  // Delete FeCA record
  const handleDelete = async () => {
    try {
      await api.delete(`/fe-mgmt/fe-ca/${deleteItem.id}/`);
      setSnackbarMessage('FeCA record deleted successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchFeCAList();
    } catch {
      setSnackbarMessage('Failed to delete record.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Filter user list for CA dropdown based on search
  const filteredUsers = userList.filter(u =>
    u.email && u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', bgcolor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" fontWeight="bold">FeCA Master</Typography>
        <Button variant="contained" color="primary" onClick={handleAdd}>Add New FeCA</Button>
      </Box>

      <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{
        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }
      }} />

      {/* Form Dialog */}
      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit FeCA Record' : 'Add FeCA Record'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="FE Base"
            margin="normal"
            fullWidth
            value={formData.financial_establishment}
            onChange={e => setFormData({ ...formData, financial_establishment: e.target.value })}
            error={!!formErrors.financial_establishment}
            helperText={formErrors.financial_establishment}
          >
            {feBaseList.map(fe => (
              <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search CA by Email"
            margin="normal"
            fullWidth
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            placeholder="Type to search CA email..."
          />
          <TextField
            select
            label="Assign CA (Email)"
            margin="normal"
            fullWidth
            value={formData.ca}
            onChange={e => setFormData({ ...formData, ca: e.target.value })}
            error={!!formErrors.ca}
            helperText={formErrors.ca}
          >
            {filteredUsers.length ? (
              filteredUsers.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.email}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No matching users</MenuItem>
            )}
          </TextField>
          <TextField
            select
            label="Active"
            margin="normal"
            fullWidth
            value={formData.is_active ? 'true' : 'false'}
            onChange={e => setFormData({ ...formData, is_active: e.target.value === 'true' })}
          >
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this FeCA record?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};


